import React from 'react';
import { Box, Typography, Container, Grid, Paper, Avatar, Divider, IconButton, Button, Chip, Card, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import AnimatedCode from '../components/AnimatedCode';
import TerminalIcon from '@mui/icons-material/Terminal';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import LanguageIcon from '@mui/icons-material/Language';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent 0%, ${theme.palette.divider} 50%, transparent 100%)`,
  },
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  border: `1px solid ${theme.palette.divider}`,
}));

const HeaderBanner = styled(Box)(({ theme }) => ({
  height: 180,
  backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}90, ${theme.palette.secondary.main}90)`,
  position: 'relative',
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  padding: theme.spacing(0, 3, 3, 3),
  backgroundColor: theme.palette.background.paper,
}));

const ProfileImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: -60,
  marginRight: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginRight: 0,
    marginBottom: theme.spacing(2),
  },
}));

const ProfileImage = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
}));

const ProfileInfo = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
  },
}));

const ProfileSocial = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    top: 'auto',
    right: 'auto',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#fff',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    transform: 'translateY(-2px)',
  },
}));

const ContentSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  border: `1px solid ${theme.palette.divider}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(1.5),
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 50,
    height: 3,
    backgroundColor: theme.palette.primary.main,
  },
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.05)',
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}));

const ExperienceItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(4),
  '&:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const CompanyLogo = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.08)' 
    : 'rgba(0, 0, 0, 0.04)',
  color: theme.palette.primary.main,
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const ExperienceContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
}));

const SkillsGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const SkillCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
    '& .icon-wrapper': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
    },
  },
}));

const SkillIconWrapper = styled(Box)(({ theme }) => ({
  width: 50,
  height: 50,
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.04)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  color: theme.palette.primary.main,
  className: 'icon-wrapper',
}));

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const experienceItems = [
    {
      title: 'Senior Backend Engineer',
      company: 'Tech Innovations Inc.',
      period: '2021 - Present',
      description: 'Leading the development of distributed systems handling millions of requests daily. Architected microservices ecosystems that reduced system latency by 40%. Implemented robust CI/CD pipelines and automated deployment strategies.',
      icon: <BusinessIcon />,
    },
    {
      title: 'Backend Developer',
      company: 'DataScale Solutions',
      period: '2018 - 2021',
      description: 'Developed high-performance APIs and database solutions. Optimized query performance resulting in 60% faster response times. Implemented scalable cloud infrastructure using AWS and Kubernetes.',
      icon: <BusinessIcon />,
    },
    {
      title: 'Software Engineer',
      company: 'CloudNet Systems',
      period: '2016 - 2018',
      description: 'Built backend services for cloud-based applications. Implemented efficient data processing pipelines. Contributed to open source projects focused on backend development.',
      icon: <BusinessIcon />,
    },
  ];

  const skills = [
    {
      icon: <TerminalIcon />,
      title: 'Backend Development',
      description: 'Building robust APIs, services, and systems that power modern applications with performance and security in mind.',
    },
    {
      icon: <StorageIcon />,
      title: 'Database Design',
      description: 'Crafting optimized database schemas and query patterns for both SQL and NoSQL databases to ensure data integrity and performance.',
    },
    {
      icon: <CloudIcon />,
      title: 'Cloud Infrastructure',
      description: 'Implementing scalable, fault-tolerant architectures on AWS, with containerization using Docker and orchestration with Kubernetes.',
    },
    {
      icon: <CodeIcon />,
      title: 'Clean Code Practices',
      description: 'Following SOLID principles, writing well-tested code, and designing maintainable software architectures for long-term success.',
    },
  ];

  const personalInfo = [
    { icon: <LocationOnIcon />, label: 'Location', value: 'New Delhi, IN' },
    { icon: <LanguageIcon />, label: 'Languages', value: 'English, Hindi' },
    { icon: <EmailIcon />, label: 'Contact', value: 'ak21367185@gmail.com' },
  ];

  const educationItems = [
    {
      school: 'Uttarakhand Technical University',
      degree: 'Bachelor of Technology, Computer Science and Engineering',
      period: '2016 - 2020',
      description: 'Graduated with honors. Active member of the Computer Science Club and contributed to multiple research projects in software engineering.',
      icon: <SchoolIcon />,
    },
  ];

  return (
    <SectionContainer id="about">
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <ProfileHeader>
            <HeaderBanner>
              <ProfileSocial>
                <SocialButton aria-label="LinkedIn" href="https://linkedin.com/in/kumarav1nash" target="_blank">
                  <LinkedInIcon />
                </SocialButton>
                <SocialButton aria-label="GitHub" href="https://github.com/kumarav1nash" target="_blank">
                  <GitHubIcon />
                </SocialButton>
                {/* <SocialButton aria-label="Twitter" href="https://twitter.com/kumarav1nash" target="_blank">
                  <TwitterIcon />
                </SocialButton> */}
              </ProfileSocial>
            </HeaderBanner>
            
            <ProfileImageContainer>
              <ProfileImageWrapper>
                <ProfileImage
                  src="/images/profile.jpg" 
                  alt="Avinash Kumar"
                />
              </ProfileImageWrapper>
              
              <ProfileInfo>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  Avinash Kumar
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{mt: -2, mb: 1 }} style={{fontWeight: 600,color: '#666'}}>
                  @kumarav1nash
                </Typography>
                
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Senior Software Developer @Petco | Driving Real-Time Innovation for 50M+ Pet Lovers 🐾                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {personalInfo.map((info, index) => (
                    <InfoChip
                      key={index}
                      icon={info.icon}
                      label={info.value}
                      size="small"
                    />
                  ))}
                </Box>
              </ProfileInfo>
            </ProfileImageContainer>
          </ProfileHeader>

          <motion.div variants={itemVariants}>
            <ContentSection>
              <SectionTitle variant="h5">
                About Me
              </SectionTitle>
              
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                I'm a passionate software engineer with a deep interest in building scalable and 
                efficient systems. My journey in technology has been driven by curiosity and a 
                desire to solve complex problems that impact users worldwide.
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                With over 5 years of experience across different domains, I've focused on creating robust 
                backend architectures and distributed systems that can handle millions of requests while 
                maintaining performance and reliability.
              </Typography>
              
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                I believe that great software isn't just about technical excellence, but about empathy for users 
                and collaboration with teammates. Outside of coding, I enjoy Indulging in my hobbies like trekking, 
                martial arts, and playing games. I love travelling and exploring new places.
              </Typography>
            </ContentSection>
          </motion.div>
          
          {/* <motion.div variants={itemVariants}>
            <ContentSection>
              <SectionTitle variant="h5">
                Professional Experience
              </SectionTitle>
              
              {experienceItems.map((item, index) => (
                <ExperienceItem key={index}>
                  <CompanyLogo>
                    {item.icon}
                  </CompanyLogo>
                  
                  <ExperienceContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {item.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.company} • {item.period}
                    </Typography>
                    
                    <Typography variant="body1">
                      {item.description}
                    </Typography>
                  </ExperienceContent>
                </ExperienceItem>
              ))}
            </ContentSection>
          </motion.div> */}
          
          <motion.div variants={itemVariants}>
            <ContentSection>
              <SectionTitle variant="h5">
                Education
              </SectionTitle>
              
              {educationItems.map((item, index) => (
                <ExperienceItem key={index}>
                  <CompanyLogo>
                    {item.icon}
                  </CompanyLogo>
                  
                  <ExperienceContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {item.school}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.degree} • {item.period}
                    </Typography>
                    
                    <Typography variant="body1">
                      {item.description}
                    </Typography>
                  </ExperienceContent>
                </ExperienceItem>
              ))}
            </ContentSection>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <ContentSection>
              <SectionTitle variant="h5">
                Core Skills
              </SectionTitle>
              
              <SkillsGrid container spacing={3}>
                {skills.map((skill, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <SkillCard elevation={0}>
                      <SkillIconWrapper className="icon-wrapper">
                        {skill.icon}
                      </SkillIconWrapper>
                      
                      <Typography variant="h6" gutterBottom fontWeight={600}>
                        {skill.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        {skill.description}
                      </Typography>
                    </SkillCard>
                  </Grid>
                ))}
              </SkillsGrid>
            </ContentSection>
          </motion.div>
        </motion.div>
      </Container>
    </SectionContainer>
  );
};

export default About;